/**
 * Token bridge between the API client and the auth store.
 *
 * `src/lib/api.ts` reads the current access token through `getAccessToken()`,
 * asks for a silent refresh via `attemptRefresh()` on a 401, and falls back to
 * `notifyUnauthorized()` when that fails. `src/lib/auth.tsx` owns all three —
 * it pushes the token in via `setAccessToken()` and registers the handlers.
 * Keeping this in a tiny standalone module avoids a circular import between the
 * client and the React auth provider.
 */

const STORAGE_ACCESS = 'yd_admin_access';
const STORAGE_REFRESH = 'yd_admin_refresh';
const STORAGE_USER = 'yd_admin_user';

let accessToken: string | null = null;
let onUnauthorized: (() => void) | null = null;
let onRefresh: (() => Promise<boolean>) | null = null;
let inFlight: Promise<boolean> | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

export function setRefreshHandler(
  handler: (() => Promise<boolean>) | null,
): void {
  onRefresh = handler;
}

/**
 * Exchange the stored refresh token for a fresh pair. Resolves true when the
 * caller may retry its request.
 *
 * At most ONE refresh is ever in flight; concurrent callers share the same
 * promise. This is not an optimisation — the server rotates refresh tokens and
 * revokes the whole family on reuse (auth.service.ts:286). The Overview page
 * fires six queries at once, so six independent refreshes would mean one
 * success and five "reuse detected", signing the operator out harder than the
 * expiry itself did. See 18_ADMIN_SESSION.md §2.
 */
export function attemptRefresh(): Promise<boolean> {
  inFlight ??= (onRefresh?.() ?? Promise.resolve(false))
    // Resolve false rather than reject, whatever the handler does. `api.ts`
    // awaits this inside `if (... && (await attemptRefresh()))`, with no catch:
    // a rejection there would skip `notifyUnauthorized()` entirely and throw a
    // raw Error out of the typed client, so the operator would never be signed
    // out and every screen would show an untyped failure. The handler in
    // auth.tsx happens to catch everything today — this makes that a property
    // of the contract instead of a property of the one current caller.
    .catch(() => false)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Invoked by the API client when a 401 could not be recovered by a refresh. */
export function notifyUnauthorized(): void {
  onUnauthorized?.();
}

export const authStorageKeys = {
  access: STORAGE_ACCESS,
  refresh: STORAGE_REFRESH,
  user: STORAGE_USER,
} as const;
